import { Fragment } from "react";

import { formatCurrency, formatToHumanReadable } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";

import { DiscountTemplate } from "@/types/billing/discount";

interface PackageContentsProps {
  /** The package's discount templates (its contents). Archived templates are ignored. */
  discountTemplates?: DiscountTemplate[];
  className?: string;
  /**
   * When set, render this text instead of nothing when the package has no
   * (non-archived) contents — e.g. "No discounts". Leave unset to render nothing.
   */
  emptyLabel?: string;
}

/** What a single template applies to: a specific item, a category, an item type,
 * or — when it has no specific target but still grants a discount — all items. */
function getContentLabel(template: DiscountTemplate): string | null {
  if (template.item?.name) return template.item.name;
  if (template.category) return `Any ${template.category}`;
  if (template.itemType)
    return `Any ${formatToHumanReadable(template.itemType)}`;
  if (
    (template.percentage != null && template.percentage > 0) ||
    (template.amount != null && template.amount > 0)
  ) {
    return "All items";
  }
  return null;
}

/**
 * How the package affects this content. A fully-covered (100% / prepaid) item is
 * just "included", so it gets no annotation; partial discounts are spelled out.
 */
function getDiscountAnnotation(template: DiscountTemplate): string | null {
  // Billing treats a template with both a percentage and an amount as a
  // fixed-amount discount, so check amount first to match what is actually applied.
  if (template.amount != null && template.amount > 0) {
    return `${formatCurrency(template.amount)} off`;
  }
  if (template.percentage != null && template.percentage > 0) {
    return template.percentage >= 100 ? null : `${template.percentage}% off`;
  }
  return null;
}

/**
 * Lists what's inside a package as a single inline, comma-separated line, e.g.
 * "3× Botox Treatment, 1× Signature Facial ($10 off)". When the package has no
 * (non-archived) contents, renders `emptyLabel` if one is provided (e.g. "No
 * discounts"), otherwise renders nothing.
 */
export function PackageContents({
  discountTemplates,
  className,
  emptyLabel,
}: PackageContentsProps) {
  const rows = (discountTemplates ?? [])
    .filter((template) => !template.isArchived)
    .map((template) => ({
      id: template.id,
      label: getContentLabel(template),
      quantity: template.quantity,
      annotation: getDiscountAnnotation(template),
    }))
    .filter((row): row is typeof row & { label: string } => row.label !== null);

  if (rows.length === 0) {
    if (!emptyLabel) return null;
    return (
      <p
        className={cx(
          "ml-3 mt-1 text-tiny leading-[14px] text-foreground-muted",
          className,
        )}
      >
        {emptyLabel}
      </p>
    );
  }

  return (
    <p
      className={cx(
        "ml-3 mt-1 text-tiny leading-[14px] text-foreground-muted",
        className,
      )}
    >
      {rows.map((row, index) => (
        <Fragment key={row.id}>
          <span>{index > 0 && ", "}</span>
          <span>
            {/* quantity 0 and null both mean "unlimited" — show no count prefix */}
            <span>{row.quantity ? `${row.quantity}× ` : ""}</span>
            {row.label}
          </span>
          {row.annotation && (
            <span className="text-status-success-text">
              {" "}
              ({row.annotation})
            </span>
          )}
        </Fragment>
      ))}
    </p>
  );
}
